import { isBoolean, isEmpty, isEqual, merge, omit, pickBy, uniq } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useDamageAttrSelections } from 'components/damages/withDamages';
import { Modal } from 'components/ui';
import { type AtlasGqlAsset, useGetDamageSchemaNamesQuery } from 'types/atlas-graphql';
import { useFeatures } from 'utils/features';

import { AttributesForm } from './AttributesForm';
import { type ExternalAttrSelection, resolveExternalAttrSelection } from './externalSelections';
import { type DamageEditPayload } from './types';

interface Props {
  backgroundColor?: string;
  damage: {
    attrs: Record<string, any>;
  };
  formLayout?: string;
  ignoreWorkInProgress?: boolean;
  isImageViewer?: boolean;
  loading: boolean;
  onCancel?: () => void;
  onSave?: (payload: DamageEditPayload) => void | Promise<void>;
  onAttrsChange?: (attrs: Record<string, any>) => void;
  editAttrs: Record<string, any>;
  potentialAttrs: Record<string, any>;
  potentialAssets: {
    likely: AtlasGqlAsset[];
    nearby: AtlasGqlAsset[];
  };
  onRequestDeltaTMeasurement?: () => void;
  deltaTMeasurementEnabled?: boolean;
  externalAttrSelections?: ExternalAttrSelection[];
  onExternalAttrSelectionsApplied?: (requestIds: number[]) => void;
  isNewObservation?: boolean;
  canChangeAsset?: boolean;
  canChangeSchema?: boolean;
  assetId?: string;
  schemaId?: string;
  critical?: boolean;
  shapeChanged: boolean;
  shapeDrawn: boolean;
  shouldHideRealtimePotentialAttrs?: boolean;
  showComment?: boolean;
}

export default function EditableDamageAttributes({
  backgroundColor,
  damage,
  formLayout = 'vertical',
  ignoreWorkInProgress = false,
  isImageViewer = false,
  loading,
  onCancel,
  onSave,
  onAttrsChange,
  editAttrs,
  potentialAttrs,
  potentialAssets,
  onRequestDeltaTMeasurement,
  deltaTMeasurementEnabled = false,
  externalAttrSelections = [],
  onExternalAttrSelectionsApplied,
  isNewObservation = false,
  canChangeAsset = false,
  canChangeSchema = false,
  assetId,
  schemaId,
  critical,
  shapeChanged,
  shapeDrawn,
  shouldHideRealtimePotentialAttrs = false,
  showComment,
}: Props) {
  const [saving, setSaving] = useState<boolean>(false);
  const [hasChanged, setHasChanged] = useState<boolean>(false);
  const [combinationsSatisfied, setCombinationsSatisfied] = useState<boolean>(false);
  const [workingAssetId, setWorkingAssetId] = useState<string | undefined>();
  const [workingSchemaId, setWorkingSchemaId] = useState<string | undefined>();
  const [workingCritical, setWorkingCritical] = useState<boolean | undefined>();
  const [warningModalOpen, setWarningModalOpen] = useState<boolean>(false);
  const [payload, setPayload] = useState<DamageEditPayload>({});
  const [localCurrentAttrs, setLocalCurrentAttrs] = useState<Record<string, any>>({});
  const appliedAttrRequestIdsRef = useRef<Set<number>>(new Set());
  const { DAMAGE_CRITICAL, REQUIRE_VALID_DAMAGE } = useFeatures().features;

  const { data: schemaData } = useGetDamageSchemaNamesQuery({
    variables: { ids: uniq([schemaId, workingSchemaId].filter(Boolean)) },
    skip: !schemaId,
  });

  const {
    columns,
    selections,
    hiddenSelections,
    disabledSelections,
    clearSelection,
    makeSelection,
    clearSelections,
    loading: selectableLoading,
    includesRealtimePotentialAttrs,
  } = useDamageAttrSelections({
    schemaId: workingSchemaId,
    currentAttrs: isNewObservation ? { ...editAttrs, ...localCurrentAttrs } : damage?.attrs,
    potentialAttrs,
    ignoreWorkInProgress,
    shouldHideRealtimePotentialAttrs,
    shouldHideHiddenAttrs: true,
    shouldHideDisabledAttrs: false,
  });

  const jsonSelections = useMemo(() => {
    return merge(
      {},
      ...[...selections, ...hiddenSelections, ...disabledSelections].map(
        ({ name, date, string, number }) => ({
          [name]: string || date || number,
        })
      )
    );
  }, [selections, hiddenSelections, disabledSelections]);

  useEffect(() => {
    if (isNewObservation && !isEqual(editAttrs, jsonSelections)) {
      onAttrsChange && onAttrsChange(jsonSelections);
    }
  }, [editAttrs, isNewObservation, onAttrsChange, jsonSelections]);

  useEffect(() => {
    if (!isNewObservation || !Object.keys(localCurrentAttrs).length) {
      return;
    }

    setLocalCurrentAttrs(current => {
      const next = Object.fromEntries(
        Object.entries(current).filter(([name, value]) => editAttrs[name] !== value)
      );
      return isEqual(current, next) ? current : next;
    });
  }, [editAttrs, isNewObservation, localCurrentAttrs]);

  useEffect(() => {
    /**
     * If filters are available for an attribute, it must have a value for currently available
     * schema combinations. `combinationsSatisfied` is used to indicate whether the form can be submitted
     * based on whether the selected values are a valid combination in the schema.
     */
    if (REQUIRE_VALID_DAMAGE) {
      setCombinationsSatisfied(
        !columns.some(
          ({ filters, key }) => !!filters.length && !Object.keys(jsonSelections).includes(key)
        )
      );
    }
  }, [columns, jsonSelections, REQUIRE_VALID_DAMAGE]);

  // set assetId
  useEffect(() => {
    if (assetId) {
      setWorkingAssetId(assetId);
    }
  }, [assetId]);

  // set schemaId
  useEffect(() => {
    if (schemaId) {
      setWorkingSchemaId(schemaId);
    }
  }, [schemaId]);

  // set critical status
  useEffect(() => {
    if (isBoolean(critical)) {
      setWorkingCritical(critical);
    }
  }, [critical]);

  // Need this setState to avoid react state update on unmounted component issues
  useEffect(() => {
    return () => {
      setSaving(false);
    };
  }, [warningModalOpen]);

  useEffect(() => {
    if (!damage?.attrs && !Object.keys(jsonSelections).length) {
      return;
    }

    // Allow saving if:
    // - the shape has changed
    // - the schema has changed
    // - the current critical status is different
    // - the current attributes are different from what has been selected (or)
    // - some of the attributes are no longer allowed (i.e. they will be deleted) (or)
    setHasChanged(
      Boolean(
        shapeChanged ||
          schemaId !== workingSchemaId ||
          critical !== workingCritical ||
          !isEqual(damage?.attrs, jsonSelections) ||
          disabledSelections.length
      )
    );
  }, [
    disabledSelections,
    jsonSelections,
    damage,
    shapeChanged,
    schemaId,
    workingSchemaId,
    critical,
    workingCritical,
  ]);

  const handleChangeWorkingSchemaId = useCallback(
    (newWorkingSchemaId: string | undefined) => {
      clearSelections();
      setLocalCurrentAttrs({});
      if (isNewObservation) {
        onAttrsChange && onAttrsChange({});
      }
      setWorkingSchemaId(newWorkingSchemaId);
    },
    [clearSelections, isNewObservation, onAttrsChange]
  );

  const handleOnChange = useCallback(
    selection => {
      const { name, ..._rest } = selection;
      const rest = pickBy(_rest, a => a !== undefined);
      const nextValue = rest.string ?? rest.number ?? rest.date;

      if (isNewObservation) {
        setLocalCurrentAttrs(current =>
          Object.keys(rest).length === 0 ? omit(current, name) : { ...current, [name]: nextValue }
        );
      }

      if (Object.keys(rest).length === 0) {
        clearSelection({ name });
      } else {
        makeSelection(selection);
      }
    },
    [makeSelection, clearSelection, isNewObservation]
  );

  useEffect(() => {
    if (!externalAttrSelections.length) {
      return;
    }

    const appliedRequestIds: number[] = [];

    externalAttrSelections.forEach(externalAttrSelection => {
      if (appliedAttrRequestIdsRef.current.has(externalAttrSelection.requestId)) {
        return;
      }

      const resolvedSelection = resolveExternalAttrSelection(columns, externalAttrSelection);
      if (!resolvedSelection) {
        return;
      }

      appliedAttrRequestIdsRef.current.add(externalAttrSelection.requestId);
      appliedRequestIds.push(externalAttrSelection.requestId);

      if (isNewObservation) {
        setLocalCurrentAttrs(current => ({
          ...current,
          [externalAttrSelection.name]: resolvedSelection.nextValue,
        }));
      }

      makeSelection(resolvedSelection.selection);
    });

    if (appliedRequestIds.length) {
      onExternalAttrSelectionsApplied?.(appliedRequestIds);
    }
  }, [
    columns,
    externalAttrSelections,
    isNewObservation,
    makeSelection,
    onExternalAttrSelectionsApplied,
  ]);

  const _handleSave = useCallback(
    async (payload: DamageEditPayload) => {
      if (onSave) {
        setSaving(true);
        await onSave(payload);
        setSaving(false);
      }
    },
    [onSave]
  );

  const handleSave = useCallback(
    (payload: DamageEditPayload) => {
      const { assetId, schemaId: schemaIdToSave, critical, comment } = payload;

      const _payload = {
        assetId,
        schemaId: schemaIdToSave,
        critical,
        attrs: [...selections, ...hiddenSelections],
        comment,
      };

      if (canChangeSchema && schemaId && schemaIdToSave !== schemaId) {
        setPayload(_payload);
        setWarningModalOpen(true);
      } else {
        _handleSave(_payload);
      }
    },
    [selections, schemaId, canChangeSchema, _handleSave, hiddenSelections]
  );

  const handleCancel = useCallback(() => onCancel?.(), [onCancel]);

  const sourceSchemaName = (schemaData?.damageSchemas ?? []).find(
    ({ id }) => id === schemaId
  )?.name;
  const targetSchemaName = (schemaData?.damageSchemas ?? []).find(
    ({ id }) => id === workingSchemaId
  )?.name;

  const warningModalCopy =
    sourceSchemaName && targetSchemaName
      ? `You have changed the damage schema for this damage from ${sourceSchemaName} to ${targetSchemaName}. Any damage attributes from ${sourceSchemaName} that are not in ${targetSchemaName} will be removed upon saving. Do you want to continue?`
      : 'You have changed the damage schema for this damage. Any damage attributes from the current schema that are not in the new schema will be removed upon saving. Do you want to continue?';

  const isSaveDisabled = Boolean(
    !shapeDrawn ||
      !onSave ||
      !hasChanged ||
      (REQUIRE_VALID_DAMAGE && !combinationsSatisfied) ||
      (canChangeSchema && !workingSchemaId) ||
      (canChangeAsset && !workingAssetId)
  );
  return (
    <>
      <AttributesForm
        loading={loading || selectableLoading}
        saving={saving}
        selections={jsonSelections}
        columns={columns}
        onSave={onSave ? handleSave : null}
        onCancel={handleCancel}
        onChange={handleOnChange}
        saveDisabled={isSaveDisabled}
        showComment={showComment}
        formLayout={formLayout}
        saveButtonText={isEmpty(damage) ? 'Create' : 'Save'}
        canChangeAsset={canChangeAsset}
        assetId={assetId || workingAssetId}
        setAssetId={setWorkingAssetId}
        canChangeSchema={canChangeSchema}
        schemaId={workingSchemaId || schemaId}
        setSchemaId={handleChangeWorkingSchemaId}
        canChangeCritical={DAMAGE_CRITICAL}
        critical={workingCritical}
        setCritical={setWorkingCritical}
        includesRealtimePotentialAttrs={includesRealtimePotentialAttrs}
        potentialAttrs={potentialAttrs}
        potentialAssets={potentialAssets}
        backgroundColor={backgroundColor}
        isImageViewer={isImageViewer}
        onRequestDeltaTMeasurement={onRequestDeltaTMeasurement}
        deltaTMeasurementEnabled={deltaTMeasurementEnabled}
      />
      <Modal
        // @ts-ignore
        cancelButtonProps={{ 'data-testid': 'damage-confirm-cancelBtn' }}
        cancelText="Cancel"
        confirmLoading={saving}
        destroyOnClose
        // @ts-ignore
        okButtonProps={{ 'data-testid': 'damage-confirm-confirmBtn' }}
        okText="Continue"
        okType="danger"
        onCancel={() => setWarningModalOpen(false)}
        onOk={() => _handleSave(payload)}
        title="Change Schema?"
        visible={warningModalOpen}
      >
        {warningModalCopy}
      </Modal>
    </>
  );
}
